/* eslint-disable max-len */
/**
 * @typedef UserType
 * @type {"unverified"|"guest"|"free"|"gold"|"goldFree"|"goldPromo"|"verifiedRoomModerator"|"coach"|"userModerator"|"siteModerator"|"admin"} userType
 */
/* eslint-enable max-len */

/**
 * @typedef AccessLevel
 * @type {number}
 */

/**
 * @example <caption>How to use AccessLevelService</caption>
 * At the bottom of file all possible access levels are exported and need to use them with AccessLevelService
 * #1
 * Simple check to see if user is admin with .is method
 * import AccessLevelService, { admin } from '../../../services/AccessLevelService';
 * AccessLevelService.is(admin, accessLevel) will return true if the user is admin (if he has accessLevel = 60)
 * @see is method
 *
 * #2
 * Simple check if user is not admin with .isNot method
 * import AccessLevelService, { admin } from '../../../services/AccessLevelService';
 * AccessLevelService.isNot(admin, accessLevel) will return true if the user is not admin
 * @see isNot method
 *
 * #3
 * Simple check if user is any type of gold with .isAnyGold method
 * import AccessLevelService from '../../../services/AccessLevelService';
 * AccessLevelService.isAnyGold(accessLevel) - no need to provide user type
 * @see isAnyGold method
 *
 * #4
 * In case that you need to limit some feature only for users that are less than 40 level
 * import AccessLevelService, { goldPromo } from '../../../services/AccessLevelService';
 * goldPromo with 33 accessLevel is the heighest in range so we can simple check only if max level access is 33
 * AccessLevelService.isMax(goldPromo, accessLevel)
 * @see isMax method
 *
 * #5
 * In case that you need to check if user is 40+ level we can use .isMin method
 * import AccessLevelService, { verifiedRoomModerator } from '../../../services/AccessLevelService';
 * AccessLevelService.isMin(verifiedRoomModerator, accessLevel)
 * verifiedRoomModerator is the minimal level in range 40
 * isMin method will start looking from verifiedRoomModerator up to top access level which is admin
 * @see isMin method
 *
 * #6
 * In case that you need to limit feature only for different access levels
 * for example only for guests and free users use isAny method
 * import AccessLevelService, { guest, free } from '../../../services/AccessLevelService';
 * AccessLevelService.isAny([guest, free], accessLevel)
 * @see isAny method
 */

export const userTypes = {
  unverified: 0,
  guest: 5,
  free: 10,
  gold: 30,
  goldFree: 31,
  goldPromo: 33,
  perfomer: 40,
  verifiedRoomModerator: 46,
  coach: 48,
  userModerator: 47,
  siteModerator: 50,
  admin: 60,
};

/**
 * Class AccessLevelService utility class for managing user access levels
 * {@link https://cam4com.atlassian.net/wiki/spaces/~46043407/pages/1223655594/CAM4+Access+levels+2019 documentation}
 */
class AccessLevelService {
  /**
   * @method is Check if provided userType matches accessLevel
   * @param {UserType} userType
   * @param {AccessLevel} accessLevel User accessLevel received from login / get user info request
   * @example <caption>AccessLevel Values by userType</caption>
   * unverified: 0
   * guest: 5,
   * free: 10,
   * gold: 30,
   * goldFree: 31,
   * goldPromo: 33,
   * verifiedRoomModerator: 46,
   * coach: 48,
   * userModerator: 47,
   * siteModerator: 50,
   * admin: 60
   * @returns {boolean} - true or false if accessLevel matches userType
   */
  is = (userType, accessLevel) => {
    this._validateAccessLevelType(accessLevel);
    this._validateAccessLevelName(userType);

    return this._isEqual(userTypes[userType])(accessLevel);
  };

  /**
   * @method isNot Check if provided userType does not matches accessLevel
   * @param {UserType} userType
   * @param {AccessLevel} accessLevel User accessLevel received from login / get user info request
   * @example <caption>const isNotAdmin = isNot('admin', accessLevel)</caption>
   * @example <caption>AccessLevel Values by userType</caption>
   * unverified: 0
   * guest: 5,
   * free: 10,
   * gold: 30,
   * goldFree: 31,
   * goldPromo: 33,
   * verifiedRoomModerator: 46,
   * coach: 48,
   * userModerator: 47,
   * siteModerator: 50,
   * admin: 60
   * @returns {boolean} - true or false if accessLevel matches userType
   */
  isNot = (userType, accessLevel) => {
    this._validateAccessLevelName(userType);
    this._validateAccessLevelType(accessLevel);

    return this._isNotEqual(userTypes[userType])(accessLevel);
  };

  /**
   * @method isMin It will include all accessLevels from provided userType (including it) to the highest one
   * @param {UserType} userType
   * @param {AccessLevel} accessLevel User accessLevel received from login / get user info request
   * @example <caption>AccessLevel Values by userType</caption>
   * unverified: 0
   * guest: 5,
   * free: 10,
   * gold: 30,
   * goldFree: 31,
   * goldPromo: 33,
   * verifiedRoomModerator: 46,
   * coach: 48,
   * userModerator: 47,
   * siteModerator: 50,
   * admin: 60
   * @returns {boolean} - true or false if accessLevel matches userType
   */
  isMin = (userType, accessLevel) => {
    this._validateAccessLevelName(userType);
    this._validateAccessLevelType(accessLevel);

    return Object.values(userTypes)
      .filter(this._isGreaterOrEqual(userTypes[userType]))
      .some(this._isEqual(accessLevel));
  };

  /**
   * @method isMax It will include all accessLevels from down bottom to the provided userType (including it)
   * @param {UserType} userType
   * @param {AccessLevel} accessLevel User accessLevel received from login / get user info request
   * @example <caption>AccessLevel Values by userType</caption>
   * unverified: 0
   * guest: 5,
   * free: 10,
   * gold: 30,
   * goldFree: 31,
   * goldPromo: 33,
   * verifiedRoomModerator: 46,
   * coach: 48,
   * userModerator: 47,
   * siteModerator: 50,
   * admin: 60
   * @returns {boolean} - true or false if accessLevel matches userType
   */
  isMax = (userType, accessLevel) => {
    this._validateAccessLevelName(userType);
    this._validateAccessLevelType(accessLevel);

    return Object.values(userTypes).filter(this._isLessOrEqual(userTypes[userType])).some(this._isEqual(accessLevel));
  };

  /**
   * @method isAny It will check provided accessLevel only with provided userTypes in array
   * @param {UserType[]} userTypes Array of user types
   * @param {AccessLevel} accessLevel User accessLevel received from login / get user info request
   * @example <caption>AccessLevel Values by userType</caption>
   * unverified: 0
   * guest: 5,
   * free: 10,
   * gold: 30,
   * goldFree: 31,
   * goldPromo: 33,
   * verifiedRoomModerator: 46,
   * coach: 48,
   * userModerator: 47,
   * siteModerator: 50,
   * admin: 60
   * @returns {boolean} - true or false if accessLevel matches userType
   */
  isAny = (types, accessLevel) => {
    this._validateArrayOfAccessLevelNames(types);

    return Object.keys(userTypes)
      .filter((type) => types.includes(type))
      .some((type) => userTypes[type] === accessLevel);
  };

  /**
   * It will check if provided accessLevel matches one of the gold userTypes
   * gold: 30,
   * goldFree: 31,
   * goldPromo: 33,
   * @param {AccessLevel} accessLevel User accessLevel received from login / get user info request
   * @returns {boolean} - true or false if accessLevel matches userType
   */
  isAnyGold = (accessLevel) => {
    this._validateAccessLevelType(accessLevel);

    return [userTypes.gold, userTypes.goldFree, userTypes.goldPromo].some(this._isEqual(accessLevel));
  };

  /**
   * @private
   * @returns {boolean} - check if target value is greater or equal
   */
  _isGreaterOrEqual = (targetValue) => (value) => value >= targetValue;

  /**
   * @private
   * @returns {boolean} - check if target value is less or equal
   */
  _isLessOrEqual = (targetValue) => (value) => value <= targetValue;

  /**
   * @private
   * @returns {boolean} - check if values are equal
   */
  _isEqual = (targetValue) => (value) => value === targetValue;

  /**
   * @private
   * @returns {boolean} - check if values are not equal
   */
  _isNotEqual = (targetValue) => (value) => value !== targetValue;

  /**
   * @private
   * @param {AccessLevel} accessLevel User accessLevel received from login / get user info request
   * @throws {TypeError}
   */
  _validateAccessLevelType = (accessLevel) => {
    if (typeof accessLevel !== 'number') {
      throw TypeError(`Wrong parameter accessLevel, expected a number, got ${typeof accessLevel}`);
    }
  };

  /**
   * @private
   * @param {UserType} userType
   * @throws {TypeError}
   */
  _validateAccessLevelName = (userType) => {
    if (typeof userTypes[userType] === 'undefined') {
      const userTypesArr = Object.keys(userTypes);

      throw TypeError(`Wrong parameter type, expect to be one of userType names ${userTypesArr.join(',')}`);
    }
  };

  /**
   * @private
   * @throws {TypeError}
   */
  _validateArrayOfAccessLevelNames = (types) => {
    if (!Array.isArray(types)) {
      throw TypeError(`Wrong parameter types, expect to be an array, received ${typeof types}`);
    }

    types.forEach((type) => this._validateAccessLevelName(type));
  };
}

export const unverified = 'unverified';
export const guest = 'guest';
export const general = 'general';
export const free = 'free';
export const gold = 'gold';
export const goldFree = 'goldFree';
export const goldPromo = 'goldPromo';
export const verifiedRoomModerator = 'verifiedRoomModerator';
export const coach = 'coach';
export const userModerator = 'userModerator';
export const siteModerator = 'siteModerator';
export const admin = 'admin';

export default new AccessLevelService();
